import { z } from "zod";
import { CLAVES } from "../sat/catalogos.js";
/**
 * Catálogo de servicios propios.
 *
 * Es la plantilla de la que salen los conceptos de una cotización o de una
 * factura: descripción, clave del SAT, unidad y tasas de impuesto ya
 * resueltas. Sin esto, cada cotización obliga a recapturar la ClaveProdServ
 * a mano, que es donde se cuelan los errores que el SAT rechaza.
 */
export const CATEGORIAS_SERVICIO = {
    DESARROLLO: "Desarrollo de software",
    MOVIL: "Aplicaciones móviles",
    WEB: "Sitios y plataformas web",
    IA: "Inteligencia artificial",
    DATOS: "Datos y analítica",
    INFRAESTRUCTURA: "Infraestructura y nube",
    CONSULTORIA: "Consultoría",
    SOPORTE: "Soporte y mantenimiento",
    DISENO: "Diseño de producto",
};
/**
 * Nombres cortos para las pestañas.
 *
 * Con los nombres largos, nueve categorías no caben en una fila y la barra
 * termina con scroll horizontal: la mitad de las categorías queda escondida,
 * que es exactamente lo que las pestañas venían a resolver.
 */
export const CATEGORIAS_SERVICIO_CORTO = {
    DESARROLLO: "Desarrollo",
    MOVIL: "Móvil",
    WEB: "Web",
    IA: "IA",
    DATOS: "Datos",
    INFRAESTRUCTURA: "Nube",
    CONSULTORIA: "Consultoría",
    SOPORTE: "Soporte",
    DISENO: "Diseño",
};
export const CLAVES_CATEGORIA = Object.keys(CATEGORIAS_SERVICIO);
/**
 * Unidades que implican una tarifa estable: se cobra lo mismo por hora, por
 * día o por mes sin importar el trato.
 */
const UNIDADES_TARIFA_FIJA = ["HUR", "DAY", "MON"];
/**
 * ¿El precio del catálogo se respeta tal cual, o se negocia en cada trato?
 *
 * Los recurrentes y los que se cobran por tiempo tienen tarifa: el editor de
 * cotizaciones los prellena y normalmente no se tocan. Los servicios por
 * proyecto se cotizan caso por caso según alcance, así que ahí el precio del
 * catálogo es a lo mucho una referencia.
 */
export const tienePrecioFijo = (servicio) => servicio.esRecurrente || UNIDADES_TARIFA_FIJA.includes(servicio.claveUnidad);
const monto = z.string().regex(/^\d+(\.\d{1,6})?$/, "monto inválido");
const tasa = z
    .string()
    .regex(/^\d+(\.\d{1,6})?$/, "tasa inválida")
    .nullable()
    .optional();
export const servicioCamposSchema = z.object({
    /** Código corto para buscarlo al armar una cotización, p.ej. DEV-MEDIDA. */
    clave: z
        .string()
        .trim()
        .toUpperCase()
        .min(2)
        .max(30)
        .regex(/^[A-Z0-9-]+$/, "solo letras, números y guiones"),
    nombre: z.string().trim().min(1).max(200),
    /**
     * Va tal cual al CFDI. Conviene que se explique sola en la factura: es lo
     * que el cliente y su contador van a leer meses después.
     */
    descripcion: z.string().trim().min(1).max(1000),
    categoria: z.enum(CLAVES_CATEGORIA),
    claveProdServ: z.string().regex(/^\d{8}$/, "la clave del SAT tiene 8 dígitos"),
    claveUnidad: z.string().trim().min(1).max(20),
    unidad: z.string().trim().max(20).optional(),
    precioBase: monto.nullable().optional(),
    moneda: z.enum(CLAVES.moneda).default("MXN"),
    objetoImp: z.enum(CLAVES.objetoImp).default("02"),
    tasaIva: tasa.default("0.160000"),
    /**
     * Las retenciones dependen de QUIÉN recibe la factura, no del servicio:
     * solo aplican cuando una persona física le factura a una moral. Lo que se
     * guarda aquí es la tasa sugerida; el valor definitivo se decide al emitir.
     */
    tasaRetIva: tasa,
    tasaRetIsr: tasa,
    /** Servicios que se cobran mes con mes: soporte, hospedaje, retainers. */
    esRecurrente: z.boolean().default(false),
    notas: z.string().max(4000).optional(),
});
export const servicioCreateSchema = servicioCamposSchema;
export const servicioUpdateSchema = servicioCamposSchema
    .partial()
    .extend({ activo: z.boolean().optional() });
export const listaServiciosQuerySchema = z.object({
    q: z.string().trim().optional(),
    categoria: z.enum(CLAVES_CATEGORIA).optional(),
    activo: z.stringbool().optional(),
    recurrente: z.stringbool().optional(),
    limite: z.coerce.number().int().min(1).max(300).default(200),
});
//# sourceMappingURL=servicio.js.map